import React, { useState, useEffect, useRef, Fragment } from "react";
import { UserAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { Button } from "components/buttons";
import { FormTextInput } from "components/form-fields";
import { ListingTable } from "components/listing";
import { Tooltip } from "components/tooltip";
import { ShowHide } from "components/layout";
import { NotificationToaster } from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { useDebounce } from "hooks/useDebounce";
import { PcnService } from "services/PcnService";
import { PcnStatusModal } from "pages/pcn/admin/settings/PcnStatusModal";

const requiredAction = "Internal";
const pageSize = 50;
const debounceTimeout = 750;
const badRequestCode = 400;
const generalErrorMessage = "An error occurred while loading statuses. Please try again.";
const protectedStatus = "pending";
const protectedTooltipMessage = "This status is system-managed and cannot be modified.";

const defaultModel = { id: 0, statusName: "", openStatus: false, reviewDays: null };
const sortableHeaders = [
    { headerName: "Status Name", sortName: "STATUSNAME" },
    { headerName: "Open", sortName: "OPENSTATUS" },
    { headerName: "Review Days", sortName: "REVIEWDAYS" }
];
const tableHeaders = ["Status Name", "Open", "Review Days", "Actions"];

export function PcnStatusesTab() {
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const [loading, setLoading] = useState(true);
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [sortedBy, setSortedBy] = useState(sortableHeaders[0]);
    const [sortedDir, setSortedDir] = useState("A");
    const [showModal, setShowModal] = useState(false);
    const [isModalUpdate, setIsModalUpdate] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(defaultModel);
    const inputSearchRef = useRef(null);

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
        setPageNumber(1);
    }

    function onSort(header, direction) {
        setSortedBy(header);
        setSortedDir(direction);
        setPageNumber(1);
    }

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function addStatus() {
        setIsModalUpdate(false);
        setSelectedStatus(defaultModel);
        setShowModal(true);
    }

    function onEditStatus(id) {
        setModalLoading(true);
        setIsModalUpdate(true);
        setShowModal(true);
        PcnService.getStatus(id)
            .then((details) => {
                setSelectedStatus(details);
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not load the status. Please try again.");
                onCloseModal();
            })
            .finally(() => {
                setModalLoading(false);
            });
    }

    function onCloseModal() {
        setShowModal(false);
        setSelectedStatus(defaultModel);
        setIsModalUpdate(false);
    }

    function onSaveModal() {
        onCloseModal();
        fetchData();
    }

    function renderEditControl(row) {
        // The "Pending" status is system-managed and must not be editable; the
        // comparison is case-insensitive to match the backend's protection rule.
        const isProtected = (row.statusName ?? "").toLowerCase() === protectedStatus;
        return (
            <Tooltip content={isProtected ? protectedTooltipMessage : "Edit status"}>
                <Button onClick={() => onEditStatus(row.id)} icon="edit" iconOnly={true} minimal={true} large={false} disabled={isProtected} />
            </Tooltip>
        );
    }

    function fetchData() {
        setLoading(true);
        const searchRequest = { requestCount: pageSize, pageNumber: pageNumber, searchTerm: debouncedSearchTerm, sortField: sortedBy.sortName, sortDirection: sortedDir };
        PcnService.searchStatuses(searchRequest)
            .then((response) => {
                const rows = (response.data ?? []).map((row) => {
                    return [row.statusName, row.openStatus ? "Yes" : "No", row.reviewDays, <div key={`${row.id}-actions`}>{renderEditControl(row)}</div>];
                });
                setTableRows(rows);
                setTotalRecords(response.totalCount);
            }, (error) => {
                if (error?.response?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.response.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, generalErrorMessage);
                }
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, generalErrorMessage);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        fetchData();
    }, [debouncedSearchTerm, pageNumber, sortedBy, sortedDir]);

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <Fragment>
                    <div className="spacer-bottom">
                        <Button intent="primary" text="Add new status" id="add-status" onClick={addStatus} loading={loading} />
                    </div>
                    <FormTextInput inputRef={inputSearchRef} placeholder="Search statuses" disabled={loading && inputSearchRef.current !== document?.activeElement} onChange={onSearchChange} value={searchTerm} large icon="search" id="status-search-field" fill />
                    <ListingTable id="listing-table-statuses" headers={tableHeaders} data={tableRows} totalRecordCount={totalRecords} loadingData={loading} onPagingChange={onPagingChange} startPage={pageNumber} pageSize={pageSize} showPerPage={false} sortable sortableHeaders={sortableHeaders} sortedBy={sortedBy} sortedDir={sortedDir} onSort={onSort} noDataMessage="No statuses to display" />
                    <ShowHide evaluator={showModal} show={(
                        <PcnStatusModal showModal={showModal} modalTitle={isModalUpdate ? "Update Status" : "Add New Status"} isUpdate={isModalUpdate} loading={modalLoading} onSave={onSaveModal} onCloseModal={onCloseModal} statusDetails={selectedStatus} />
                    )} />
                </Fragment>
            )}
            no={() => (<div className="row"><PageRestricted /></div>)}
        />
    );
}
